import { useEffect } from "react";
import API_BASE_URL from "../config";

/**
 * Desktop only; renders nothing. Reminders and appointments become OS
 * notifications — the point of putting them in Photentra is not having to keep
 * the app open to be reminded, and on desktop the tray keeps us alive to do it.
 *
 * Polls every 5 minutes; fires when an item is due within the next 30 minutes;
 * remembers what it has fired (localStorage) so nothing notifies twice.
 */
const POLL_MS = 5 * 60 * 1000;
const DUE_WINDOW_MS = 30 * 60 * 1000;
const FIRED_KEY = "photentra_desktop_notified";

export default function DesktopNotifications() {
	useEffect(() => {
		if (!window.photentra?.notify) return undefined;

		const check = async () => {
			const token = localStorage.getItem("token");
			if (!token) return;
			const headers = { Authorization: `Bearer ${token}` };

			let reminders = [];
			let appointments = [];
			try {
				const [remRes, apptRes] = await Promise.all([
					fetch(`${API_BASE_URL}/api/ai/reminders`, { headers }),
					fetch(`${API_BASE_URL}/api/ai/appointments`, { headers }),
				]);
				if (remRes.ok) reminders = (await remRes.json()).reminders || [];
				if (apptRes.ok) appointments = (await apptRes.json()).appointments || [];
			} catch {
				return; // offline — next poll will catch up
			}

			const fired = readFired();
			const now = Date.now();

			const consider = (item, kind, date, time) => {
				if (!date) return;
				if (item.status === "completed") return;
				const key = `${kind}_${item.id}`;
				if (fired.has(key)) return;

				const dueAt = toTimestamp(date, time);
				if (!dueAt) return;
				// Fire inside the window; also catch things that just became overdue
				// while the machine was asleep (up to one window in the past).
				if (dueAt - now > DUE_WINDOW_MS || now - dueAt > DUE_WINDOW_MS) return;

				window.photentra.notify(
					kind === "reminder" ? "Reminder" : "Appointment",
					`${item.title || "Untitled"} — ${new Date(dueAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`,
				);
				fired.add(key);
			};

			for (const r of reminders) consider(r, "reminder", r.reminder_date, r.reminder_time);
			for (const a of appointments) consider(a, "appointment", a.appointment_date, a.appointment_time);

			writeFired(fired);
		};

		check();
		const interval = setInterval(check, POLL_MS);
		return () => clearInterval(interval);
	}, []);

	return null;
}

function toTimestamp(date, time) {
	const day = new Date(date);
	if (Number.isNaN(day.getTime())) return null;
	if (time) {
		const [h, m] = String(time).split(":");
		day.setHours(Number(h) || 0, Number(m) || 0, 0, 0);
	} else {
		day.setHours(9, 0, 0, 0); // date-only items announce at 9am
	}
	return day.getTime();
}

function readFired() {
	try {
		return new Set(JSON.parse(localStorage.getItem(FIRED_KEY) || "[]"));
	} catch {
		return new Set();
	}
}

function writeFired(set) {
	// Cap the memory of past notifications — old keys never fire again anyway
	// once their due window has passed.
	localStorage.setItem(FIRED_KEY, JSON.stringify([...set].slice(-500)));
}
